import { useEffect } from 'react';

const ORDER_LOOKUP_TIMEOUT_MS = 30000;
const ORDER_LOOKUP_RETRY_DELAYS_MS = [2000, 2000, 3000, 3000, 3000];
const RETRYABLE_ERROR_CODES = new Set([
  'ORDER_NOT_READY',
  'USER_NOT_READY',
  'ORDER_LOOKUP_UNAVAILABLE',
  'STRIPE_RATE_LIMITED',
  'STRIPE_UNAVAILABLE',
]);

const getOrderLookupRetryDelay = (attempt) => ORDER_LOOKUP_RETRY_DELAYS_MS[attempt] || 3000;

export const isOrderLookupRetryable = (result) => {
  if (result?.data?.error) return false;

  const errorCode = result?.error?.data?.code;
  if (errorCode) return RETRYABLE_ERROR_CODES.has(errorCode);

  const status = result?.error?.status;
  if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') return true;
  if (typeof status !== 'number') return false;

  return status === 404 || status === 408 || status === 429 || status >= 500;
};

const useOrderLookupRetry = ({ isEnabled, lookup, onOrderFound, lookupLabel }) => {
  useEffect(() => {
    if (!isEnabled) return undefined;

    let isCancelled = false;
    let retryTimer = null;
    const startedAt = Date.now();

    const scheduleNextLookup = (attempt, lookupOrder) => {
      const remainingTime = ORDER_LOOKUP_TIMEOUT_MS - (Date.now() - startedAt);

      if (remainingTime <= 0) {
        console.log('Order lookup timed out:', lookupLabel);
        return;
      }

      const delay = Math.min(getOrderLookupRetryDelay(attempt), remainingTime);
      retryTimer = setTimeout(() => lookupOrder(attempt + 1), delay);
    };

    const lookupOrder = async (attempt = 0) => {
      if (isCancelled) return;

      let result;
      try {
        result = await lookup();
      } catch (error) {
        console.log('Order lookup request failed:', lookupLabel, error);
        if (!isCancelled) scheduleNextLookup(attempt, lookupOrder);
        return;
      }

      if (isCancelled) return;

      const order = result?.data?.data;
      if (order) {
        try {
          await onOrderFound(order);
        } catch (error) {
          console.log('Error handling found order:', error);
        }
        return;
      }

      if (!isOrderLookupRetryable(result)) {
        console.log('Order lookup stopped after a terminal response:', lookupLabel, result?.error || result?.data);
        return;
      }

      if (!isCancelled) scheduleNextLookup(attempt, lookupOrder);
    };

    lookupOrder();

    return () => {
      isCancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [isEnabled, lookup, lookupLabel, onOrderFound]);
};

export default useOrderLookupRetry;
